import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import MuiTypography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import Typography from '@/v2/components/common/Typography'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { copyToClipboard } from '@/v2/utils/clipboard'

// An id plus a one-click copy button, so staff can quote a claim to support or
// to the payer without retyping it.
const CopyableId = ({ label, value }) => {
  const { onOpenSnackbar } = useSnackbarContext()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(timer)
  }, [copied])

  const handleCopy = async () => {
    if (await copyToClipboard(value)) {
      setCopied(true)
      onOpenSnackbar(`${label} copied to clipboard.`)
    } else {
      onOpenSnackbar(`It wasn't possible to copy the ${label}.`, 'error')
    }
  }

  return (
    <Box display="flex" alignItems="center" gap="4px">
      <MuiTypography variant="body1" color="text.secondary">
        {label}: <strong>{value}</strong>
      </MuiTypography>
      <Tooltip title={copied ? 'Copied' : `Copy ${label}`}>
        <IconButton
          size="small"
          aria-label={`Copy ${label}`}
          onClick={handleCopy}
          sx={{ padding: '2px' }}
        >
          {copied ? (
            <CheckIcon fontSize="small" color="success" />
          ) : (
            <ContentCopyIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  )
}

// Replaces the modal's plain title so the claim's ids sit right under it. The
// DXC id only exists once the claim has been submitted to the clearinghouse.
// The negative bottom margin trims the header wrapper's 21px bottom padding,
// which reads as dead space now that the ids add a second line.
const ClaimModalHeader = ({ title, claimId, dxcClaimId }) => (
  <Box
    display="flex"
    flexDirection="column"
    paddingRight="32px"
    marginBottom={claimId ? '-10px' : 0}
  >
    <Typography id="modal-header-title" variant="h3" fontWeight="500">
      {title}
    </Typography>
    {!!claimId && (
      <Box display="flex" alignItems="center" flexWrap="wrap" columnGap="16px">
        <CopyableId label="Internal Claim ID" value={claimId} />
        {!!dxcClaimId && <CopyableId label="DXC ID" value={dxcClaimId} />}
      </Box>
    )}
  </Box>
)

export default ClaimModalHeader
